/**
 * Next AR due date metrics — shared by CSV Merge header and future reporting pages.
 * Uses assignment.nextARDueDate (YYYY-MM-DD), aligned with csvMergeReleaseService / dueForAR.
 */

import { todayYmd } from './taskUtils';

export const DEFAULT_AR_DUE_HORIZON_DAYS = 30;

/** @param {string|undefined|null} ymd */
export function normalizeNextArDueYmd(ymd) {
  if (!ymd) return null;
  const trimmed = String(ymd).trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : null;
}

/** @param {object|null|undefined} client */
export function getClientNextArDueYmd(client) {
  return normalizeNextArDueYmd(client?.assignment?.nextARDueDate);
}

/** Local-calendar add days; returns YYYY-MM-DD. */
export function addDaysYmd(ymd, days) {
  const base = normalizeNextArDueYmd(ymd);
  if (!base) return null;
  const [y, mo, d] = base.split('-').map(Number);
  const date = new Date(y, mo - 1, d);
  date.setDate(date.getDate() + days);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** True when next AR due is today or earlier. */
export function isNextArOverdue(dueYmd, asOfYmd = todayYmd()) {
  const due = normalizeNextArDueYmd(dueYmd);
  const asOf = normalizeNextArDueYmd(asOfYmd);
  if (!due || !asOf) return false;
  return due <= asOf;
}

/** True when due on or before asOf + horizonDays (includes overdue). */
export function isNextArDueWithinHorizon(dueYmd, asOfYmd = todayYmd(), horizonDays = DEFAULT_AR_DUE_HORIZON_DAYS) {
  const due = normalizeNextArDueYmd(dueYmd);
  const asOf = normalizeNextArDueYmd(asOfYmd);
  if (!due || !asOf) return false;
  const horizonEnd = addDaysYmd(asOf, horizonDays);
  if (!horizonEnd) return false;
  return due <= horizonEnd;
}

/** Local-calendar add months (clamped to the month's last day); returns YYYY-MM-DD. */
export function addMonthsYmd(ymd, months) {
  const base = normalizeNextArDueYmd(ymd);
  if (!base) return null;
  const [y, mo, d] = base.split('-').map(Number);
  const target = new Date(y, mo - 1 + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(d, lastDay));
  const month = String(target.getMonth() + 1).padStart(2, '0');
  const day = String(target.getDate()).padStart(2, '0');
  return `${target.getFullYear()}-${month}-${day}`;
}

/**
 * Annual reviews open for booking two months before the due date (Advice
 * History "Opens for booking"): true when next AR due is on or before
 * asOf + 2 months, overdue included.
 */
export function isNextArDueForBooking(dueYmd, asOfYmd = todayYmd(), months = 2) {
  const due = normalizeNextArDueYmd(dueYmd);
  const horizonEnd = addMonthsYmd(asOfYmd, months);
  if (!due || !horizonEnd) return false;
  return due <= horizonEnd;
}

/**
 * @param {object[]} clients
 * @param {{ asOfYmd?: string, horizonDays?: number }} [options]
 * @returns {{ overdue: number, dueWithinHorizon: number, withDueDate: number, withoutDueDate: number }}
 */
export function countClientsByNextArDue(clients, options = {}) {
  const asOfYmd = normalizeNextArDueYmd(options.asOfYmd) || todayYmd();
  const horizonDays = options.horizonDays ?? DEFAULT_AR_DUE_HORIZON_DAYS;

  let overdue = 0;
  let dueWithinHorizon = 0;
  let withDueDate = 0;
  let withoutDueDate = 0;

  for (const client of clients || []) {
    const due = getClientNextArDueYmd(client);
    if (!due) {
      withoutDueDate += 1;
      continue;
    }
    withDueDate += 1;
    if (isNextArOverdue(due, asOfYmd)) overdue += 1;
    if (isNextArDueWithinHorizon(due, asOfYmd, horizonDays)) dueWithinHorizon += 1;
  }

  return { overdue, dueWithinHorizon, withDueDate, withoutDueDate };
}
